import Phaser from 'phaser';
import { showBanner } from '../AdManager';

export default class SplashScene extends Phaser.Scene {
  constructor() {
    super({ key: 'SplashScene' });
  }

  create() {
    showBanner();
    const W   = this.scale.width;
    const H   = this.scale.height;
    const dpr = window.devicePixelRatio || 1;
    const S   = Math.min((W / dpr) / 420, 1.8);
    const sd  = S * dpr;

    // ── Cotton candy background ───────────────────────────────────
    const bg = this.add.graphics();
    bg.fillGradientStyle(0xf8f0ff, 0xebe8ff, 0xf0e8ff, 0xf8f0ff, 1);
    bg.fillRect(0, 0, W, H);

    // Soft bloom orbs
    const bloom = this.add.graphics();
    [
      { x: W * 0.15, y: H * 0.18, r: W * 0.55, col: 0xff9ec5, a: 0.12 },
      { x: W * 0.90, y: H * 0.12, r: W * 0.50, col: 0x99ccff, a: 0.11 },
      { x: W * 0.50, y: H * 0.72, r: W * 0.55, col: 0xcc99ff, a: 0.09 },
    ].forEach(({ x, y, r, col, a }) => {
      for (let i = 4; i >= 1; i--) {
        bloom.fillStyle(col, (a / 4) * i);
        bloom.fillCircle(x, y, r * (i / 4));
      }
    });

    // Corner cotton candy puffs
    [
      { x: W * 0.03, y: H * 0.18, col: 0xcc99ff, sc: 0.9 * S },
      { x: W * 0.80, y: H * 0.12, col: 0xaad4ff, sc: 0.8 * S },
      { x: W * 0.88, y: H * 0.72, col: 0xcc99ff, sc: 0.7 * S },
      { x: W * 0.02, y: H * 0.74, col: 0xcc99ff, sc: 0.8 * S },
    ].forEach(pd => {
      const g = this.add.graphics();
      this.drawPuff(g, pd.col, pd.sc);
      g.x = pd.x; g.y = pd.y; g.alpha = 0.75;
    });

    // Sparkle dots scattered on bg
    const dotColors = [0xffffff, 0xffcce6, 0xccddff, 0xeeccff];
    const dots = this.add.graphics();
    for (let i = 0; i < 45; i++) {
      dots.fillStyle(Phaser.Math.RND.pick(dotColors), Phaser.Math.FloatBetween(0.15, 0.60));
      dots.fillCircle(Phaser.Math.Between(0, W), Phaser.Math.Between(0, H), Phaser.Math.FloatBetween(1, 2.8));
    }

    // ── Logo group — fades + scales in ───────────────────────────
    const logoY = H * 0.40;

    // White glow circle behind logo text
    const glow = this.add.graphics().setAlpha(0);
    glow.fillStyle(0xffffff, 0.45);
    glow.fillCircle(W / 2, logoY + 8 * sd, 105 * sd);
    glow.fillStyle(0xddbbff, 0.20);
    glow.fillCircle(W / 2, logoY + 8 * sd, 135 * sd);

    // Orbiting food emojis
    const emojiDefs = ['🍕', '🍦', '🍩', '🌮', '🍔', '🍪'];
    const emojiObjs = emojiDefs.map((e, i) => {
      const angle = Phaser.Math.DegToRad((i / emojiDefs.length) * 360 - 90);
      const r     = Math.min(W * 0.36, 148 * sd);
      const obj   = this.add.text(
        W / 2 + Math.cos(angle) * r,
        logoY + Math.sin(angle) * r,
        e,
        { fontSize: `${20 * sd}px`, fontFamily: 'Arial' }
      ).setOrigin(0.5).setAlpha(0);
      return obj;
    });

    // "Food" headline
    const foodTxt = this.add.text(W / 2, logoY - 30 * sd, 'Food', {
      fontSize:        `${74 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#9933cc',
      stroke:          '#f0e8ff',
      strokeThickness: 7 * dpr,
    }).setOrigin(0.5).setAlpha(0).setScale(0.6);

    // "Stack" subheading
    const stackTxt = this.add.text(W / 2, logoY + 46 * sd, 'Stack', {
      fontSize:        `${58 * sd}px`,
      fontFamily:      'Georgia, "Times New Roman", serif',
      fontStyle:       'bold italic',
      color:           '#aa66ff',
      stroke:          '#ddd0ff',
      strokeThickness: 6 * dpr,
    }).setOrigin(0.5).setAlpha(0).setScale(0.6);

    // Pink decorative line under title
    const deco = this.add.graphics().setAlpha(0);
    deco.lineStyle(3 * dpr, 0xbb88ee, 0.9);
    deco.lineBetween(W / 2 - 75 * sd, logoY + 82 * sd, W / 2 + 75 * sd, logoY + 82 * sd);

    // Tagline
    const tagline = this.add.text(W / 2, H * 0.68, 'How high can you stack?', {
      fontSize:  `${17 * sd}px`,
      fontFamily:'Georgia, "Times New Roman", serif',
      fontStyle: 'italic',
      color:     '#9966cc',
    }).setOrigin(0.5).setAlpha(0);

    // Twinkling sparkles around logo
    const sparkles = Array.from({ length: 8 }, (_, i) => {
      const angle = (i / 8) * Math.PI * 2;
      const dist  = Phaser.Math.Between(90, 145) * sd;
      const sz    = Phaser.Math.FloatBetween(3, 7) * sd;
      const col   = [0xffffff, 0xddbbff, 0xccddff][i % 3];
      const sp    = this.add.graphics().setAlpha(0);
      sp.fillStyle(col, 1);
      sp.fillRect(-sz / 6, -sz / 2, sz / 3, sz);
      sp.fillRect(-sz / 2, -sz / 6, sz, sz / 3);
      sp.x = W / 2 + Math.cos(angle) * dist;
      sp.y = logoY  + Math.sin(angle) * dist;
      return sp;
    });

    // ── Animation sequence ────────────────────────────────────────
    // Glow fades in
    this.tweens.add({ targets: glow, alpha: 1, duration: 400, ease: 'Power2' });

    // "Food" pops in
    this.tweens.add({
      targets: foodTxt, alpha: 1, scaleX: 1, scaleY: 1,
      duration: 500, delay: 100, ease: 'Back.easeOut',
    });
    // "Stack" follows
    this.tweens.add({
      targets: stackTxt, alpha: 1, scaleX: 1, scaleY: 1,
      duration: 500, delay: 250, ease: 'Back.easeOut',
    });
    // Deco line
    this.tweens.add({ targets: deco, alpha: 1, duration: 300, delay: 450, ease: 'Power2' });
    // Emojis pop in one by one
    emojiObjs.forEach((obj, i) => {
      this.tweens.add({
        targets: obj, alpha: 1, scaleX: { from: 0.3, to: 1 }, scaleY: { from: 0.3, to: 1 },
        duration: 300, delay: 350 + i * 70, ease: 'Back.easeOut',
      });
    });
    // Tagline fades in
    this.tweens.add({ targets: tagline, alpha: 1, duration: 400, delay: 700, ease: 'Power2' });
    // Sparkles twinkle
    sparkles.forEach((sp, i) => {
      this.tweens.add({
        targets: sp, alpha: { from: 0, to: 0.9 },
        scaleX: { from: 0.2, to: 1 }, scaleY: { from: 0.2, to: 1 },
        duration: 400, delay: 500 + i * 80, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      });
    });
    // Gentle logo float
    this.tweens.add({
      targets: [foodTxt, stackTxt, deco, glow],
      y: '-=6', duration: 2000, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
    });

    // Hold then exit
    this.time.delayedCall(3000, () => {
      this.tweens.add({
        targets: [glow, foodTxt, stackTxt, deco, tagline, ...emojiObjs, ...sparkles],
        alpha: 0, duration: 500, ease: 'Power2',
        onComplete: () => this.scene.start('HomeScene'),
      });
    });
  }

  drawPuff(gfx, col, scale) {
    gfx.fillStyle(col, 0.28);
    [
      { ox:   0, oy:  0, r: 32 }, { ox:  30, oy: -8, r: 26 },
      { ox: -30, oy: -6, r: 24 }, { ox:  14, oy:-22, r: 20 },
      { ox: -14, oy:-20, r: 19 }, { ox:  46, oy:  5, r: 18 },
      { ox: -46, oy:  5, r: 17 },
    ].forEach(b => gfx.fillCircle(b.ox * scale, b.oy * scale, b.r * scale));
    gfx.fillStyle(0xffffff, 0.18);
    gfx.fillCircle(-6 * scale, -12 * scale, 18 * scale);
  }
}
